// src/apiUtils/uplynkCentralInitiator.js
import { uplynkCentralTrackCallStatus } from './uplynkCentralTrackCallStatus';
import { getValueByPath } from './objectUtils';
/**
 * Centralized function to initiate API tracking workflows
 * 
 * @param {Object} data - The response data from API call
 * @param {Object} config - Complete configuration for both initiation and tracking
 * @returns {void}
 */
// const uplynkCentralInitiator = (data, config) => {
  export const initiateJobTracking = (data, config) => {

  // Start a single console group for all logging
  console.group("UPLYNK CENTRAL INITIATOR");
  
  try {
    // Log initial data and config
    console.log("Input data:", JSON.stringify(data, null, 2).substring(0, 500) + "...");
    console.log("Input config:", config);
    console.log("Callbacks exist:", !!config.callbacks);
    if (config.callbacks) {
      console.log("Callback functions:", {
        setProcessRunning: typeof config.callbacks.setProcessRunning === 'function',
        onProgressCallback: typeof config.callbacks.onProgressCallback === 'function',
        updateResults: typeof config.callbacks.updateResults === 'function'
      });
    }
    console.log("Credentials:", config.resources?.credentials);
    console.log(`Processing response for path: ${data.node?.path}`);

    // Extract configurations
    const triggerConfig = config.trigger || {};
    const trackingConfig = config.tracking || {};
    
    // Check if basic response status is successful
    const successPath = triggerConfig.successPath || 'node.response.status';
    const successValue = triggerConfig.successValue || 'success';
    const actualValue = getValueByPath(data, successPath);
    
    console.log(`Checking success condition: ${successPath} = ${successValue}? Actual: ${actualValue}`);
    
    if (actualValue !== successValue) {
      console.log(`Response status check failed: expected "${successValue}", got "${actualValue}"`);
      console.groupEnd();
      return;
    }
    
    // Check if path matches the configured path
    if (triggerConfig.path && data.node?.path !== triggerConfig.path) {
      console.log(`Path doesn't match: ${data.node?.path} vs ${triggerConfig.path}`);
      console.groupEnd();
      return;
    }
    
    // Extract tracking ID directly
    console.group("Job ID Extraction");
    console.log("Looking for job ID in:", data.node?.response?.data);
    
    // Directly extract the job ID - don't rely on path resolution for critical path
    let trackingId = null;
    if (data.node?.response?.data?.job?.id) {
      trackingId = data.node.response.data.job.id;
      console.log("Found job ID:", trackingId);
    } else {
      // Fall back to configured path
      console.log("Job ID not found in standard location");
      console.log("Trying configured path:", trackingConfig.idPath);
      trackingId = getValueByPath(data, trackingConfig.idPath);
      console.log("Result from path lookup:", trackingId);
    }
    console.groupEnd();
    
    // Check if we got a tracking ID
    if (!trackingId) {
      console.log("Failed to extract tracking ID");
      console.groupEnd();
      return;
    }
    
    // Verify all required fields exist
    const requiredFields = triggerConfig.requiresFields || [];
    const missingFields = [];
    
    console.group("Required Fields Check");
    for (const field of requiredFields) {
      console.log(`Checking field: ${field}`);
      // Check directly in response data first
      let value = data.node?.response?.data?.job ? 
                  getValueByPath(data.node.response.data.job, field.replace(/^job\./, '')) : 
                  undefined;
                  
      // If not found, try full path
      if (value === undefined) {
        value = getValueByPath(data, field);
      }
      
      if (value === undefined) {
        console.log(`Field not found: ${field}`);
        missingFields.push(field);
      } else {
        console.log(`Field found: ${field} = ${value}`);
      }
    }
    console.groupEnd();
    
    if (missingFields.length > 0) {
      console.log(`Missing required fields: ${missingFields.join(', ')}`);
      console.groupEnd();
      return;
    }
    
    // All conditions are met, proceed with tracking
    console.log("All checks passed, starting tracking process");
    
    // Extract callbacks configuration
    const callbacks = config.callbacks || {};
    
    // Set process running state
    if (callbacks.setProcessRunning) {
      callbacks.setProcessRunning(true);
      console.log(`Started tracking process for: ${triggerConfig.path}`);
    }
    
    // Start tracking with the tracker function
    console.log("Starting tracking for job ID:", trackingId);
    uplynkCentralTrackCallStatus(
      trackingId,
      trackingConfig.targetStates || ['done', 'error'],
      {
        credentials: config.resources?.credentials,
        abortController: config.resources?.abortControllerRef?.current,
        
        onProgress: (progressData) => {
          console.log('Polling - uplynkCentralTrackCallStatus - Progress Start');
          // Map fields according to configuration
          const progressUpdate = {};
          
          // Add timestamp and attempt number
          progressUpdate.timestamp = new Date();
          if (progressData.attempt !== undefined) {
            progressUpdate.attempt = progressData.attempt;
          }
          
          // Call progress callback if configured
          if (callbacks.onProgressCallback) {
            callbacks.onProgressCallback({
              ...progressUpdate,
              ...progressData
            });
            console.log(`Polling - uplynkCentralTrackCallStatus - Progress update:`, progressData);
          }
        },
    
        
        onComplete: (finalResponse) => {
          console.log(`Job completed for ${triggerConfig.path}`);
          
          // Extract completion data
          const completionData = finalResponse.extractedData || {};
          
          // Update results if we have data and a callback
          if (Object.keys(completionData).length > 0 && callbacks.updateResults) {
            callbacks.updateResults(completionData);
            console.log(`Updated results with:`, completionData);
          }
          
          // Mark process as complete
          if (callbacks.setProcessRunning) {
            callbacks.setProcessRunning(false);
            console.log(`Completed process for: ${triggerConfig.path}`);
          }
        }
      },
      // Pass relevant tracking configuration to the tracker
      {
        api: config.api || {},
        response: config.response || {},
        states: config.states || {},
        polling: config.polling || {}
      }
    ).catch(error => {
      // Mark process as complete on error
      if (callbacks.setProcessRunning) {
        callbacks.setProcessRunning(false);
      }
      
      // Handle specific error types
      if (error.name === 'AbortError') {
        console.log(`Job tracking was aborted for: ${triggerConfig.path}`);
      } else {
        console.error(`Job tracking failed for: ${triggerConfig.path}:`, error);
      }
      
      // End the logging group on error
      console.groupEnd();
    });
    return {
      status: 'tracking_started',
      jobId: trackingId,
      targetStates: trackingConfig.targetStates || ['done', 'error']
    };
  } catch (error) {
    // Ensure process is marked as not running in case of error
    if (config.callbacks?.setProcessRunning) {
      config.callbacks.setProcessRunning(false);
    }
    console.error(`Unhandled error:`, error);
    
    // End the logging group in case of unhandled error
    console.groupEnd();
    return {
      status: 'tracking_failed',
      error: error.message
    };
    // throw error; // Re-throw for debugging
  }
};

// export default uplynkCentralInitiator;
export default initiateJobTracking; // For backward compatibility


